/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from 'react';

// reactstrap components
import {
  Button,
  Card,
  Container,
  Row,
  Col,
  Progress,
  Spinner
} from "reactstrap";

// core components
import Api from "../../components/helpers/Api";
import StatsOverview from "../stats/StatsOverview"; // Updated to StatsOverview
import '../../assets/css/picker.css';
import '../../the-worship-bible.css'; // Added CSS import
import '../../the-worship-bible-2.css'; // Added CSS import
import { RiDeleteBin6Line } from "@remixicon/react";
import { RiBookOpenLine, RiHeartFill, RiEditFill } from "react-icons/ri";
import { updatePlaylist } from "../../components/helpers/CuratedPlaylistHelpers";
import { debugLog } from "../../utils/debug";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";
import {SeoHelper} from "../../components/helpers/SeoHelper";
import KstTrackLinks from "../../components/kst/KstTrackLinks";

// index page sections

class ThemesSongs extends React.Component {
  state = {
    isLoading: false,
    listItems: [],
    selectedSongCount: 0,
    chosenThemeButtons: [], // Already present
    chosenSongBooksButtons: [] // Added for songbook pills
  }

  constructor(props) {
    super(props);
    this.state = { value: '' };

    if (sessionStorage.getItem("playlist")) {
      sessionStorage.setItem("curated-songs", JSON.stringify(JSON.parse(sessionStorage.getItem("playlist")).curatedSongs));
    }

    this.state.selectedSongCount = !sessionStorage.getItem("curated-songs") || !JSON.parse(sessionStorage.getItem("curated-songs")) ? 0 : JSON.parse(sessionStorage.getItem("curated-songs")).length;

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  showSongBookPill = (songBook, canRemove = true) => {
    return (
        <div className="songbook-pill">
          <Button className='text-uppercase' color="primary" size="sm" style={{margin: '4px', width: "100%"}}
                  title={songBook.title}>
            <span style={{fontSize: "0.6rem"}}>
              {songBook.title.length <= 22 ? songBook.title : !songBook.shortTitle ? songBook.title.substring(0, 19) + "..." : songBook.shortTitle}
            </span>
            {canRemove && (
                <a href="" title={`Remove ${songBook.title} from selected song books`} onClick={(e) => {
                  e.preventDefault();
                  this.removeSongBook(songBook);
                }} className="btn-inner--icon ml-1 mr-1 float-right">
                  <RiDeleteBin6Line size={16} color="white"/>
                </a>
            )}
          </Button>
        </div>
    );
  }

  componentDidMount() {
    this.setState({ isLoading: true });
    const themes = sessionStorage.getItem("selected-themes") == null ? [] : JSON.parse(sessionStorage.getItem("selected-themes"));
    const preferredThemes = sessionStorage.getItem("preferred-themes") == null ? [] : JSON.parse(sessionStorage.getItem("preferred-themes"));
    const requestedThemes = [...themes, ...preferredThemes].map(theme => {
      return {
        "id": theme.id,
        "title": theme.title
      }
    });
    const songBookSelection = this.getEffectiveSongBookSelection();
    const effectiveSongBookIds = songBookSelection.books.map(sb => sb.id);

    const requestData = {
      selectedSongBookIds: effectiveSongBookIds,
      themes: requestedThemes
    };

    Api.post(`/words/themes?includeSongCount=true`, requestData)
        .then(res => {
          const listItems = res.data;
          sessionStorage.setItem("songs", JSON.stringify(listItems));
          this.setState({ listItems: listItems });
          this.setState({ isLoading: false });
        })
        .catch(err => {
          console.error("Error loading theme songs:", err);
          this.setState({ isLoading: false });
        });

    const preferredSongBookIds = preferredThemes.map(psb => psb.id);
    this.setState({
      chosenThemeButtons: [...themes, ...preferredThemes].map((theme, index) => {
        return (
            <div key={index} className="theme-pill">
              <Button className='text-uppercase' color="primary" size="sm" style={{ margin: '4px', width: "100%" }} title={theme.title}>
            <span style={{ fontSize: "0.6rem" }}>
              {theme.title.length <= 22 ? theme.title : theme.title.substring(0, 19) + "..."}
            </span>
                {preferredSongBookIds && preferredSongBookIds.filter((val) => val.includes(theme.id)).length >= 1 ? (
                    <RiHeartFill size={18} style={{ color: "white", float: "right" }} />
                ) : (
                    <a href="#" title={"Remove " + theme.title + " from selected themes"} onClick={() => this.removePill(theme)} className="btn-inner--icon ml-1 mr-1 float-right">
                      <RiDeleteBin6Line size={18} style={{ color: "white", float: "right" }} />
                    </a>
                )}
              </Button>
            </div>
        );
      }),
    });
    const combined = [];

    // Deduplicate by `id`
    const uniqueSongBooks = [];
    const seenIds = new Set();

    combined.forEach(sb => {
      if (sb && !seenIds.has(sb.id)) {
        seenIds.add(sb.id);
        uniqueSongBooks.push(sb);
      }
    });

    this.setState({
      chosenSongBooksButtons: uniqueSongBooks
          .sort((sb1, sb2) => sb1.title.localeCompare(sb2.title))
          .map((songBook, index) => this.showSongBookPill(songBook))
    });

    sessionStorage.setItem("curatedSongsContinueEnabled", !sessionStorage.getItem("curated-songs") || sessionStorage.getItem("curated-songs") === 'null' ? false : JSON.parse(sessionStorage.getItem("curated-songs")).length > 0);

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'build_service', {
        source: 'themes'
      });
    }

  }

  handleChange(event) {
    this.setState({ value: event.target.value });
  }

  handleSubmit = (event) => {
    event.preventDefault();
    alert('A name was submitted: ' + this.state.value);
    event.preventDefault();
  }

  getEffectiveSongBookSelection = () => {
    const selectedBooks = JSON.parse(sessionStorage.getItem("selected-song-books") || "[]");
    const preferredBooks = JSON.parse(sessionStorage.getItem("preferred-song-books") || "[]");

    if (selectedBooks.length > 0) {
      return { books: selectedBooks, source: "selected" };
    }

    if (preferredBooks.length > 0) {
      return { books: preferredBooks, source: "preferred" };
    }

    return { books: [], source: "all" };
  };

  renderSelectedSongBooks = (songBookSelection) => {
    if (songBookSelection.books.length === 0) {
      return (
          <>
            <p>All song books {!sessionStorage.getItem("statsTtl") ? "" : "(" + JSON.parse(sessionStorage.getItem("statsTtl")).stats.find(x => x.title === "Song Books")?.value + ")"}</p>
            {sessionStorage.getItem("customer") && (
                <Button
                    color="link"
                    size="sm"
                    className="p-0 mt-2"
                    href="/your-preferences/song-books"
                >
                  Change preferred song books
                </Button>
            )}
          </>
      );
    }

    return (
        <>
          {songBookSelection.books.map((sb) => this.showSongBookPill(sb, songBookSelection.source === "selected"))}
          {sessionStorage.getItem("customer") && (
              <Button
                  color="link"
                  size="sm"
                  className="p-0 mt-2"
                  href="/your-preferences/song-books"
              >
                Change preferred song books
              </Button>
          )}
        </>
    );
  };

  getTitle = (objectWithTitle) => {
    return objectWithTitle.map((object, index) => {
      const themes = sessionStorage.getItem("selected-themes") == null ? [] : JSON.parse(sessionStorage.getItem("selected-themes"));
      return (
          <small
              className={themes.some(theme => theme.title.startsWith(object.title)) ? "font-weight-heavy" : "font-weight-light"}
              style={themes.some(theme => theme.title.startsWith(object.title)) ? { color: "#5e72e4" } : { color: "#D3D3D3" }}
          >
            {object.title}{index + 1 === objectWithTitle.length ? "" : ", "}

          </small>
      );
    });
  }

  updateCuratedListItem = async (event, song, songBook, index) => {
    let existingCuratedSongs = sessionStorage.getItem("curated-songs")
        ? JSON.parse(sessionStorage.getItem("curated-songs"))
        : [];

    const comboKey = song.id + "/" + songBook.id;
    const already = existingCuratedSongs.some(c => c.key === comboKey);

    if (!already) {
      const themes = JSON.parse(sessionStorage.getItem("selected-themes") || "[]");
      const theme = themes.map(t => t.title).join(", ");

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'select_song', {
          source: 'themes',
          theme
        });
      }

      // Build comma-separated author names for display if you like:
      const authorNames = (song.authors || []).map(a => a.name);

      let newCuratedSong = {
        key: comboKey,
        songId: song.id,
        songTitle: song.title,
        alternativeTitle: song.alternativeTitle,
        authors: song.authors,           // << store full author objects
        authorNames: authorNames.join(", "), // << or just store a display string
        songBookId: songBook.songBook.id,
        songBookTitle: songBook.songBook.title,
        songBookSongNumber: song.wordsSongBooks[index].songBookSongNumber,
        keySignature:  songBook.keySignature  || null,
        timeSignature: songBook.timeSignature || null,
        tempoBpm:      songBook.tempoBpm      ?? null,
        meter:         songBook.meter         || null,
        ccliNumber:    songBook.ccliNumber    || null,
        kstTracks: song.kstTracks || [],
        i: existingCuratedSongs.length + 1,
        playOrder: existingCuratedSongs.length + 1
      };

      existingCuratedSongs.push(newCuratedSong);
      sessionStorage.setItem("curated-songs", JSON.stringify(existingCuratedSongs));
      event.currentTarget.checked = true;
    } else {
      // remove it
      existingCuratedSongs = existingCuratedSongs.filter(c => c.key !== comboKey);
      sessionStorage.setItem("curated-songs", JSON.stringify(existingCuratedSongs));
      event.currentTarget.checked = false;
    }

    // update count & playlist if needed...
    this.setState({
      selectedSongCount: existingCuratedSongs.length
    });

    if (sessionStorage.getItem("playlist")) {
      const playlist = JSON.parse(sessionStorage.getItem("playlist"));
      const result = await updatePlaylist(
          playlist.id,
          playlist.name,
          playlist.saveNotes,
          existingCuratedSongs
      );
      // handle result...
    }

    sessionStorage.setItem(
        "curatedSongsContinueEnabled",
        existingCuratedSongs.length > 0
    );
    this.forceUpdate();
  }

  removePill = (theme) => {
    let updatedThemes = sessionStorage.getItem("selected-themes") == null
        ? []
        : JSON.parse(sessionStorage.getItem("selected-themes")).filter(t => t.id !== theme.id);
    if (!updatedThemes || updatedThemes.length === 0) {
      sessionStorage.removeItem("selected-themes");
      sessionStorage.removeItem("curated-songs");
      sessionStorage.removeItem("playlist");
    } else {
      sessionStorage.setItem("selected-themes", JSON.stringify(updatedThemes));
    }

    this.componentDidMount();
  }

  removeSongBook = (songBookToRemove) => {
    let updatedSongBooks = sessionStorage.getItem("selected-song-books") == null
        ? []
        : JSON.parse(sessionStorage.getItem("selected-song-books")).filter(sb => sb.id !== songBookToRemove.id);

    if (!updatedSongBooks || updatedSongBooks.length === 0) {
      sessionStorage.removeItem("selected-song-books");
      sessionStorage.removeItem("curated-songs");
      sessionStorage.removeItem("playlist");
    } else {
      sessionStorage.setItem("selected-song-books", JSON.stringify(updatedSongBooks));
    }

    this.componentDidMount();
  };

  isSongCuratedAlready = (songId, songBookId) => {
    let result = false;
    if (sessionStorage.getItem("curated-songs") && sessionStorage.getItem("curated-songs") != 'null' && JSON.parse(sessionStorage.getItem("curated-songs")).length > 0) {
      result = (JSON.parse(sessionStorage.getItem("curated-songs")).some(songBookCombo => songBookCombo.key === songId + "/" + songBookId));
    }
    return result;
  }

  showTitle(songBook) {
    // if songBook or its title is missing, bail out with an empty string
    if (!songBook || typeof songBook.title !== 'string') {
      return '';
    }

    const raw = songBook.title;
    const short = songBook.shortTitle;
    // pick the display title
    const titleToUse =
        raw.length <= 20
            ? raw
            : (!short ? raw.substring(0, 19) + '…' : short);

    debugLog(
        `[debug] using title ${titleToUse}, title ${raw} (${raw.length}) / shortTitle ${short}`
    );

    return <>{titleToUse}</>;
  }

  displaySortedSongBooks = (song, songBooks) => {
    if (!Array.isArray(songBooks) || songBooks.length === 0) {
      return null;
    }

    return [...songBooks].map((songBook, index) => {
      const metaParts = [];
      if (songBook.keySignature)  metaParts.push(`Key: ${songBook.keySignature}`);
      if (songBook.timeSignature) metaParts.push(`Time Signature: ${songBook.timeSignature}`);
      if (songBook.tempoBpm)      metaParts.push(`Tempo: ${songBook.tempoBpm} bpm`);
      if (songBook.meter)         metaParts.push(`Metre: ${songBook.meter}`);
      if (songBook.ccliNumber)    metaParts.push(`CCLI: ${songBook.ccliNumber}`);

      return (
          <div className="col-md-12 col-xs-12 mb-1">
            <label className="custom-toggle" style={{ marginBottom: metaParts.length > 0 ? "0" : "-30px" }}>
              <input
                  className="custom-control-input"
                  id={song.id}
                  value={song.id + "-" + songBook.id}
                  onClick={(e) => this.updateCuratedListItem(e, song, songBook, index)}
                  checked={this.isSongCuratedAlready(song.id, songBook.id) ? "checked" : ""}
                  type="radio"
              />
              <span className="custom-toggle-slider rounded-circle" />
              <span className="ml-6 mb-3" style={{ whiteSpace: "nowrap" }}>{this.showTitle(songBook.songBook)}</span>
            </label>
            {metaParts.length > 0 && (
              <div style={{ fontSize: "0.75rem", color: "#888", marginLeft: "3.5rem" }}>
                {metaParts.join(' · ')}
              </div>
            )}
          </div>
      );
    });
  }

  displaySortedSongs = () => {
    return sessionStorage.getItem("songs") == null
        ? []
        : JSON.parse(sessionStorage.getItem("songs"))
            .sort((a, b) => a.title.localeCompare(b.title))
            .map((song) => {
              // Build a comma-separated authors line
              const authorLine = (song.authors || [])
                  .map((a) => a.name)
                  .join(", ");

              return (
                  <div
                      key={song.key}
                      className={`item ${song === this.state.draggingItem ? "dragging" : ""}`}
                      draggable="false"
                  >
                    <div className="align-items-start col-md-12 col-xs-12">
                      <div className="col-md-12 col-xs-12 my-2" style={{ fontWeight: "bold" }}>
                        {/* Song Title */}
                        {song.title}
                        <KstTrackLinks kstTracks={song.kstTracks} />
                        <br />

                        {/* Authors */}
                        {authorLine && (
                            <div
                                style={{
                                  fontWeight: "normal",
                                  fontSize: "0.85rem",
                                  color: "#555",
                                  marginBottom: "0.25rem",
                                }}
                            >
                              {authorLine}
                            </div>
                        )}

                        {/* Themes */}
                        <span className="font-weight-light">
                    {this.getTitle(song.themes)}
                  </span>
                      </div>

                      {/* SongBook pills */}
                      <div className="col-md-12">
                        {this.displaySortedSongBooks(song, song.wordsSongBooks)}
                      </div>
                    </div>
                  </div>
              );
            });
  };

  nextpage = () => {
    return sessionStorage.getItem("playlist") ? "/playlist/" + JSON.parse(sessionStorage.getItem("playlist")).id + "/" + JSON.parse(sessionStorage.getItem("playlist")).name : "/themes/curated-songs";
  }

  listSongBookTitles = () => {
    return (
        <>
          {!sessionStorage.getItem("selected-song-books") ? "" :
              JSON.parse(sessionStorage.getItem("selected-song-books")).map(sb => <span><RiBookOpenLine color="#ffffff" /> {sb.title}<br /></span>)
          }
        </>
    );
  }

  render() {
      const continueButtonEnabled = !sessionStorage.getItem("curatedSongsContinueEnabled") ? false : (sessionStorage.getItem("curatedSongsContinueEnabled") === "true");
      const sortedSongs = this.displaySortedSongs();
      const nextpage = this.nextpage();
      const songBookSelection = this.getEffectiveSongBookSelection();

      return (
          <>
            <SeoHelper
                uri="/themes/songs"
                title = "Choose Songs | The Worship Bible"
                description="Discover worship songs based on specific Themes. The Worship Bible helps you find and plan song-flows rooted in biblical themes for meaningful worship services."
            />

            <div className="position-relative">
              <section className="section section-lg section-shaped pb-10">
                <div className="shape shape-style-1 shape-default">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
                <Container className="py-lg-md d-flex">
                  <div className="col px-0">
                    <Row>
                      <Col lg="12">
                        <h1 className="display-3 text-white">
                          Building your song-flows{" "}
                          <span>
                        found {!sessionStorage.songs || !JSON.parse(sessionStorage.songs) ? 0 : JSON.parse(sessionStorage.songs).length} songs
                      </span>
                        </h1>
                        <p className="lead text-white">
                          <br />
                        </p>
                      </Col>
                    </Row>
                  </div>
                </Container>
                <div className="separator separator-bottom separator-skew">
                  <svg
                      xmlns="http://www.w3.org/2000/svg"
                      preserveAspectRatio="none"
                      version="1.1"
                      viewBox="0 0 2560 100"
                      x="0"
                      y="0"
                  >
                    <polygon
                        className="fill-white"
                        points="2560 0 2560 100 0 100"
                    />
                  </svg>
                </div>
              </section>
            </div>
            <section className="section section-lg pt-lg-9">
              <Container>
                <Card className="card-profile shadow mt--300">
                  <div className="px-4">
                    <Row className="mt-3">
                      <Col md="3" xs="12">
                        <Button
                            block
                            type="submit"
                            className="mb-3"
                            color="secondary"
                            href="/themes/choose-themes"
                        >
                          <span className="btn-inner--text">Back</span>
                        </Button>
                      </Col>
                      <Col md="6" xs="12">
                      </Col>
                      <Col md="3" xs="12">
                        <div className="text-center">
                        {(
                              (sessionStorage.getItem("curated-songs") && sessionStorage.getItem("curated-songs") != 'null' && JSON.parse(sessionStorage.getItem("curated-songs")).length > 0 && !sessionStorage.getItem("playlist")) ||
                              (sessionStorage.getItem("playlist") && sessionStorage.getItem("workingOnDraft") === "true")
                          ) && <>
                          <Button
                              className="btn-icon mb-3 mb-sm-0 mx-auto float-left"
                              color="primary"
                              href="/themes/curated-songs"
                          >
                                    <span className="btn-inner--icon mr-1">
                                      <RiEditFill title="Edit" size="16"/>
                                    </span>
                              <span className="btn-inner--text">Go to song-flow</span>
                          </Button>
                        </>
                        }
                        </div>
                      </Col>
                    </Row>
                    <StatsOverview />
                    <div className="main-content-wrapper">
                      <div className="songs-content">
                        {!sessionStorage.getItem("customer") && (this.state.listItems || []).length > 0 && (
                          <div className="alert alert-light border d-flex justify-content-between align-items-center mb-3">
                            <span>
                              Planning a service? Save your song-flow and come back to it later.
                            </span>
                            <a href="/register/choose-your-plan" className="btn btn-sm btn-primary ml-3">
                              Create an account →
                            </a>
                          </div>
                        )}
                        {this.state.isLoading ? (
                            <div className="spinner-container">
                              <Spinner animation="border" role="status">
                                <span className="visually-hidden"></span>
                              </Spinner>
                              Loading...
                            </div>
                        ) : (
                            <div className="table-list" style={{ width: '100%' }}>
                              {sortedSongs}
                            </div>
                        )}
                        <div className="text-center mt-3">
                          <h2 style={{fontSize: '1rem'}}>Add additional songs</h2>
                        </div>
                        <Row>
                          <Col md="6" xs="12">
                            <Button
                                block
                                className="mb-3"
                                color="primary"
                                type="button"
                                href="/scripture/choose-passages"
                            >
                          <span className="btn-inner--icon mr-3">
                            <i className="ni ni-book-bookmark" />
                          </span>
                              Add from Scripture
                            </Button>
                          </Col>
                          <Col md="6" xs="12">
                            <Button
                                block
                                className="mb-3"
                                color="primary"
                                type="button"
                                href="/themes/choose-themes"
                            >
                          <span className="btn-inner--icon mr-3">
                            <i className="ni ni-archive-2" />
                          </span>
                              Add from Theme
                            </Button>
                          </Col>
                        </Row>
                        <Row>
                          <Col md="12">
                            <Button
                                block
                                type="submit"
                                className="mb-3"
                                color="primary"
                                href="/lectionary/choose-lectionary"
                            >
                              <span className="btn-inner--text">Add from Lectionary</span>
                            </Button>
                          </Col>
                        </Row>
                        <Row>
                          <Col md="6" xs="12">
                            <Button
                                block
                                type="submit"
                                className="mb-3"
                                color="secondary"
                                href="/themes/choose-themes"
                            >
                              <span className="btn-inner--text">Back</span>
                            </Button>
                          </Col>
                        </Row>
                      </div>
                      <div className="pills-sidebar">
                        <h2 style={{fontSize: '1rem'}}>Selected themes: {this.state.chosenThemeButtons && this.state.chosenThemeButtons.length > 0 ? "(" + this.state.chosenThemeButtons.length + ")" : <></>}</h2>
                        {this.state.chosenThemeButtons && this.state.chosenThemeButtons.length > 0 ? (
                            this.state.chosenThemeButtons
                        ) : (
                            <p>No themes selected</p>
                        )}
                        <h2 style={{fontSize: '1rem'}} className="mt-4">Selected song books: {songBookSelection.books.length > 0 ? "(" + songBookSelection.books.length + ")" : <></>}</h2>
                        {this.renderSelectedSongBooks(songBookSelection)}
                      </div>
                    </div>
                    <div style={{
                      position: "fixed",
                      bottom: "0",
                      left: "0",
                      width: "100%",
                      backgroundColor: "white",
                      padding: "40px",
                      boxShadow: "0 -2px 5px rgba(0, 0, 0, 0.2)",
                      zIndex: "1000"
                    }}>
                      <div className="col-md-12 col-xs-12" style={{ position: "fixed", bottom: "20px", right: "0px", zIndex: "1000" }}>
                        <Button
                            block
                            type="submit"
                            className="mb-3"
                            color="primary"
                            href={nextpage}
                            disabled={!continueButtonEnabled}
                        >
                          <span className="btn-inner--text">Curate Song-flow</span>
                        </Button>
                      </div>
                    </div>
                    {/*<div className="mt-5 py-5 border-top text-center">*/}
                    {/*  <div className="progress-wrapper">*/}
                    {/*    <div className="progress-info">*/}
                    {/*      <div className="progress-label">*/}
                    {/*        <span>WORSHIP SONG-FLOWS CREATION TASKS COMPLETED</span>*/}
                    {/*      </div>*/}
                    {/*      <div className="progress-percentage">*/}
                    {/*        <span>3/4</span>*/}
                    {/*      </div>*/}
                    {/*    </div>*/}
                    {/*    <Progress max="100" value="75" />*/}
                    {/*  </div>*/}
                    {/*</div>*/}
                  </div>
                </Card>
              </Container>
            </section>
          </>
      );
    }
  }

  export default ThemesSongs;
